/* eslint-disable */
import { MultilingualString } from '@salsita-ai/platform-common/utils'
import {
  DomainEntityId,
  DomainEntityReflection,
  EntityReflections,
  RawAliasReflection,
  SchemaOfObject,
  SchemaReflection,
} from '@salsita-ai/platform-project-sdk/domain'

export const modelVersion = 1

export interface GenericDomainEntity {
  readonly id: DomainEntityId<string>
  readonly type: string
  [k: string]: unknown
}
export const GenericDomainEntity: SchemaReflection<GenericDomainEntity> = new SchemaReflection('GenericDomainEntity', {
  type: 'object',
  properties: {
    id: {
      type: 'string',
      pattern: '^[A-Z]\\w*-\\d+$',
    },
    type: {
      type: 'string',
    },
  },
  additionalProperties: true,
  required: ['id', 'type'],
})

export interface RecipeConfiguration {
  readonly modelVersion: number
  readonly entities: readonly GenericDomainEntity[]
  readonly rootEntityType: string
  readonly rootRecipeId: string
  readonly recipeIds: readonly string[]
  readonly configurationId: string
  readonly revisionId: string
  readonly ownerToken: string
}
export const RecipeConfiguration: SchemaReflection<RecipeConfiguration> = new SchemaReflection('RecipeConfiguration', {
  type: 'object',
  properties: {
    modelVersion: {
      type: 'integer',
    },
    entities: {
      type: 'array',
      items: GenericDomainEntity._meta.schema,
    },
    rootEntityType: {
      type: 'string',
    },
    rootRecipeId: {
      type: 'string',
    },
    recipeIds: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    configurationId: {
      type: 'string',
    },
    revisionId: {
      type: 'string',
    },
    ownerToken: {
      type: 'string',
    },
  },
  additionalProperties: false,
  required: [
    'entities',
    'rootEntityType',
    'rootRecipeId',
    'recipeIds',
    'modelVersion',
    'configurationId',
    'revisionId',
    'ownerToken',
  ],
})

interface CommonAdminEntityShape {
  readonly commonAdminModelVersion: 4
}
const CommonAdminEntityShape = new SchemaReflection<CommonAdminEntity, SchemaOfObject<CommonAdminEntityShape>>(
  'CommonAdminEntityShape',
  {
    type: 'object',
    properties: {
      commonAdminModelVersion: {
        enum: [4],
      },
    },
    additionalProperties: false,
    required: ['commonAdminModelVersion'],
  }
)

interface PresetCategoryShape {
  readonly name: MultilingualString
  readonly index: number
  readonly visible: boolean
  readonly description: MultilingualString
}
const PresetCategoryShape = new SchemaReflection<PresetCategory, SchemaOfObject<PresetCategoryShape>>(
  'PresetCategoryShape',
  {
    type: 'object',
    properties: {
      name: {
        type: 'object',
        properties: {
          $lang: {
            type: 'object',
            additionalProperties: {
              type: 'string',
            },
          },
        },
        required: ['$lang'],
        additionalProperties: false,
      },
      index: {
        type: 'number',
      },
      visible: {
        type: 'boolean',
      },
      description: {
        type: 'object',
        properties: {
          $lang: {
            type: 'object',
            additionalProperties: {
              type: 'string',
            },
          },
        },
        required: ['$lang'],
        additionalProperties: false,
      },
    },
    additionalProperties: false,
    required: ['name', 'index', 'visible', 'description'],
  }
)

interface PresetShape {
  readonly name: MultilingualString
  readonly description: MultilingualString
  readonly index: number
  readonly visible: boolean
  readonly categoryId: PresetCategory['id'] | null
  readonly configurationId: string | null
  readonly revisionId: string | null
  readonly ownerToken: string | null
  readonly readToken: string | null
  readonly thumbnailUrl: string | null
  readonly tags: readonly string[]
}
const PresetShape = new SchemaReflection<Preset, SchemaOfObject<PresetShape>>('PresetShape', {
  type: 'object',
  properties: {
    name: {
      type: 'object',
      properties: {
        $lang: {
          type: 'object',
          additionalProperties: {
            type: 'string',
          },
        },
      },
      required: ['$lang'],
      additionalProperties: false,
    },
    description: {
      type: 'object',
      properties: {
        $lang: {
          type: 'object',
          additionalProperties: {
            type: 'string',
          },
        },
      },
      required: ['$lang'],
      additionalProperties: false,
    },
    index: {
      type: 'number',
    },
    visible: {
      type: 'boolean',
    },
    categoryId: {
      oneOf: [
        {
          type: 'string',
          pattern: '^[A-Z]\\w*-\\d+$',
        },
        {
          type: 'null',
        },
      ],
    },
    configurationId: {
      type: ['string', 'null'],
    },
    revisionId: {
      type: ['string', 'null'],
    },
    ownerToken: {
      type: ['string', 'null'],
    },
    readToken: {
      type: ['string', 'null'],
    },
    thumbnailUrl: {
      type: ['string', 'null'],
    },
    tags: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
  },
  additionalProperties: false,
  required: [
    'name',
    'description',
    'index',
    'visible',
    'categoryId',
    'configurationId',
    'revisionId',
    'ownerToken',
    'readToken',
    'thumbnailUrl',
    'tags',
  ],
})

interface DataTableShape {
  readonly dataEntryKey: string
  readonly data: {
    [k: string]: unknown
  }
}
const DataTableShape = new SchemaReflection<DataTable, SchemaOfObject<DataTableShape>>('DataTableShape', {
  type: 'object',
  properties: {
    dataEntryKey: {
      type: 'string',
    },
    data: {
      type: 'object',
    },
  },
  additionalProperties: false,
  required: ['dataEntryKey', 'data'],
})

interface ConnectedDataConfigShape {
  readonly dataEntryKey: string
  readonly provider: string
  readonly externalFileId: string
  readonly fileLabel: string
  readonly driveId: string | null
  readonly tabSelection: readonly string[]
}
const ConnectedDataConfigShape = new SchemaReflection<ConnectedDataConfig, SchemaOfObject<ConnectedDataConfigShape>>(
  'ConnectedDataConfigShape',
  {
    type: 'object',
    properties: {
      dataEntryKey: {
        type: 'string',
      },
      provider: {
        type: 'string',
      },
      externalFileId: {
        type: 'string',
      },
      fileLabel: {
        type: 'string',
      },
      driveId: {
        type: ['string', 'null'],
      },
      tabSelection: {
        type: 'array',
        items: {
          type: 'string',
        },
      },
    },
    additionalProperties: false,
    required: ['dataEntryKey', 'provider', 'externalFileId', 'fileLabel', 'driveId', 'tabSelection'],
  }
)

interface RecipeShape {
  readonly name: MultilingualString
  readonly description: MultilingualString
  readonly collection: string
  readonly index: number
  readonly visible: boolean
  readonly configuration: RecipeConfiguration | null
}
const RecipeShape = new SchemaReflection<Recipe, SchemaOfObject<RecipeShape>>('RecipeShape', {
  type: 'object',
  properties: {
    name: {
      type: 'object',
      properties: {
        $lang: {
          type: 'object',
          additionalProperties: {
            type: 'string',
          },
        },
      },
      required: ['$lang'],
      additionalProperties: false,
    },
    description: {
      type: 'object',
      properties: {
        $lang: {
          type: 'object',
          additionalProperties: {
            type: 'string',
          },
        },
      },
      required: ['$lang'],
      additionalProperties: false,
    },
    collection: {
      type: 'string',
    },
    index: {
      type: 'number',
    },
    visible: {
      type: 'boolean',
    },
    configuration: {
      anyOf: [
        RecipeConfiguration._meta.schema,
        {
          type: 'null',
        },
      ],
    },
  },
  additionalProperties: false,
  required: ['name', 'description', 'collection', 'index', 'visible', 'configuration'],
})

interface PricingSettingsShape {
  readonly data: string
}
const PricingSettingsShape = new SchemaReflection<PricingSettings, SchemaOfObject<PricingSettingsShape>>(
  'PricingSettingsShape',
  {
    type: 'object',
    properties: {
      data: {
        type: 'string',
      },
    },
    additionalProperties: false,
    required: ['data'],
  }
)

export interface Admin extends CommonAdminEntityShape {
  readonly id: DomainEntityId<'Admin'>
  readonly type: 'Admin'
}
export const Admin: DomainEntityReflection<Admin> = new DomainEntityReflection<Admin>('Admin', [
  CommonAdminEntityShape._meta.schema,
])

export interface Preset extends PresetShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'Preset'>
  readonly type: 'Preset'
}
export const Preset: DomainEntityReflection<Preset> = new DomainEntityReflection<Preset>('Preset', [
  PresetShape._meta.schema,
  CommonAdminEntityShape._meta.schema,
])

export interface PresetCategory extends PresetCategoryShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'PresetCategory'>
  readonly type: 'PresetCategory'
}
export const PresetCategory: DomainEntityReflection<PresetCategory> = new DomainEntityReflection<PresetCategory>(
  'PresetCategory',
  [PresetCategoryShape._meta.schema, CommonAdminEntityShape._meta.schema]
)

export interface Recipe extends RecipeShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'Recipe'>
  readonly type: 'Recipe'
}
export const Recipe: DomainEntityReflection<Recipe> = new DomainEntityReflection<Recipe>('Recipe', [
  RecipeShape._meta.schema,
  CommonAdminEntityShape._meta.schema,
])

export interface ConnectedDataConfig extends ConnectedDataConfigShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'ConnectedDataConfig'>
  readonly type: 'ConnectedDataConfig'
}
export const ConnectedDataConfig: DomainEntityReflection<ConnectedDataConfig> =
  new DomainEntityReflection<ConnectedDataConfig>('ConnectedDataConfig', [
    ConnectedDataConfigShape._meta.schema,
    CommonAdminEntityShape._meta.schema,
  ])

export interface DataTable extends DataTableShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'DataTable'>
  readonly type: 'DataTable'
}
export const DataTable: DomainEntityReflection<DataTable> = new DomainEntityReflection<DataTable>('DataTable', [
  DataTableShape._meta.schema,
  CommonAdminEntityShape._meta.schema,
])

export interface DummyDoNotUse {
  readonly id: DomainEntityId<'DummyDoNotUse'>
  readonly type: 'DummyDoNotUse'
}
export const DummyDoNotUse: DomainEntityReflection<DummyDoNotUse> = new DomainEntityReflection<DummyDoNotUse>(
  'DummyDoNotUse',
  []
)

export interface PricingSettings extends PricingSettingsShape {
  readonly id: DomainEntityId<'PricingSettings'>
  readonly type: 'PricingSettings'
}
export const PricingSettings: DomainEntityReflection<PricingSettings> = new DomainEntityReflection<PricingSettings>(
  'PricingSettings',
  [PricingSettingsShape._meta.schema]
)

export type AnyDomainEntity =
  | Admin
  | Preset
  | PresetCategory
  | Recipe
  | ConnectedDataConfig
  | DataTable
  | DummyDoNotUse
  | PricingSettings
export const AnyDomainEntity: RawAliasReflection<AnyDomainEntity> = new RawAliasReflection<AnyDomainEntity>(
  'AnyDomainEntity',
  [Admin, Preset, PresetCategory, Recipe, ConnectedDataConfig, DataTable, DummyDoNotUse, PricingSettings]
)

export type RootEntity = Admin
export const RootEntity: RawAliasReflection<RootEntity> = new RawAliasReflection<RootEntity>('RootEntity', [Admin])

export type CommonAdminEntity = Admin | Preset | PresetCategory | Recipe | ConnectedDataConfig | DataTable
export const CommonAdminEntity: RawAliasReflection<CommonAdminEntity> = new RawAliasReflection<CommonAdminEntity>(
  'CommonAdminEntity',
  [Admin, Preset, PresetCategory, Recipe, ConnectedDataConfig, DataTable]
)

export const aliases = {
  AnyDomainEntity: AnyDomainEntity.types,
  RootEntity: RootEntity.types,
  CommonAdminEntity: CommonAdminEntity.types,
} as const
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type Aliases = { AnyDomainEntity: AnyDomainEntity; RootEntity: RootEntity; CommonAdminEntity: CommonAdminEntity }

export const entities: EntityReflections<AnyDomainEntity> = {
  Admin,
  Preset,
  PresetCategory,
  Recipe,
  ConnectedDataConfig,
  DataTable,
  DummyDoNotUse,
  PricingSettings,
} as const

declare global {
  interface AdminModel {
    readonly Admin: Admin
    readonly Preset: Preset
    readonly PresetCategory: PresetCategory
    readonly Recipe: Recipe
    readonly ConnectedDataConfig: ConnectedDataConfig
    readonly DataTable: DataTable
    readonly DummyDoNotUse: DummyDoNotUse
    readonly PricingSettings: PricingSettings

    readonly GenericDomainEntity: GenericDomainEntity
    readonly RecipeConfiguration: RecipeConfiguration

    readonly AnyDomainEntity: AnyDomainEntity
    readonly RootEntity: RootEntity
    readonly CommonAdminEntity: CommonAdminEntity
  }
}
